import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaGithub, FaExternalLinkAlt, FaCode, FaTools } from "react-icons/fa";

import baseUrl from "../components/baseurl";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${baseUrl}/projectsget`);
        setProjects(response.data.slice(0, 4));
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const getProjectImageUrl = (project) => {
    if (!project.url && !project.repo) {
      return "https://via.placeholder.com/640x360/5271ff/ffffff?text=Project+Preview";
    }

    return `https://image.thum.io/get/width/640/crop/720/viewportWidth/1280/png/${
      project.url || project.repo
    }`;
  };

  return (
    <div className="container mx-auto px-6">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-indigo-900 mb-4">
          Featured Projects
        </h2>
        <div className="h-1 w-24 bg-indigo-500 mx-auto"></div>
        <p className="mt-6 text-gray-600 max-w-2xl mx-auto">
          Here are some of my recent projects. Each project represents my skills
          and experience in different technologies.
        </p>
      </div>

      <div className="max-w-7xl mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-16">
            <div className="relative">
              <div className="h-16 w-16 rounded-full border-t-4 border-b-4 border-indigo-500 animate-spin"></div>
              <div className="absolute top-0 left-0 h-16 w-16 rounded-full border-t-4 border-b-4 border-indigo-200 animate-spin animate-pulse"></div>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {projects?.map((project) => (
                <div
                  key={project._id}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={getProjectImageUrl(project)}
                      alt={project.comment}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                      <div className="p-6 w-full">
                        <div className="flex justify-end space-x-3">
                          {project.repo && (
                            <a
                              href={project.repo}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/40 transition-colors duration-300"
                              title="View Source Code"
                            >
                              <FaGithub className="text-white" size={20} />
                            </a>
                          )}
                          {project.url && (
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/40 transition-colors duration-300"
                              title="Visit Live Site"
                            >
                              <FaExternalLinkAlt
                                className="text-white"
                                size={20}
                              />
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3">
                      {project.name || "Project"}
                    </h3>

                    <div className="mb-4 flex items-start">
                      <FaCode className="text-indigo-600 mt-1 mr-2 flex-shrink-0" />
                      <p className="text-gray-600">{project.comment}</p>
                    </div>

                    <div className="flex items-start">
                      <FaTools className="text-indigo-600 mt-1 mr-2 flex-shrink-0" />
                      <p className="text-gray-600">{project.stack}</p>
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                      {project.stack.split(",").map((tech, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-indigo-100 text-indigo-800 text-xs font-medium rounded-full"
                        >
                          {tech.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 inline-flex items-center"
                to="/projects"
              >
                View All Projects
                <FaExternalLinkAlt className="ml-2" size={14} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
